/**
 * AI1 — Assistive AI hook (summarize · extract · suggest via gateway API).
 */

import { useCallback, useState } from 'react';
import { resolveApiOrigin } from '@shared/api/resolve-api-origin';
import { fetchWithAuth } from '../../services/authFetch';

export interface AssistiveSummarizeResult {
  suggestionId: string;
  summary: string;
  requiresHumanApproval: true;
}

export interface AssistiveSuggestResult {
  suggestionId: string;
  taskDraft: { summary: string; note?: string };
  suggestedActionIds?: readonly string[];
  requiresHumanApproval: true;
}

interface AssistiveApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export function useAssistiveAI(tenantId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [suggestResult, setSuggestResult] = useState<AssistiveSuggestResult | null>(null);

  const summarize = useCallback(
    async (sourceText: string): Promise<AssistiveSummarizeResult | null> => {
      setLoading(true);
      setError(null);
      try {
        const baseUrl = resolveApiOrigin();
        const response = await fetchWithAuth(`${baseUrl}/api/ai/summarize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenantId,
            sourceText,
            assistiveMode: 'assistive_only',
          }),
        });
        const body = (await response.json()) as AssistiveApiEnvelope<AssistiveSummarizeResult>;
        if (!response.ok || !body.success || !body.data) {
          throw new Error(body.error ?? 'AI 요약에 실패했습니다.');
        }
        setSummary(body.data.summary);
        return body.data;
      } catch (err: unknown) {
        const nextError = err instanceof Error ? err : new Error(String(err));
        setError(nextError);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [tenantId]
  );

  const suggest = useCallback(
    async (input: {
      taskName?: string;
      taskDescription?: string;
      sourceText?: string;
    }): Promise<AssistiveSuggestResult | null> => {
      setLoading(true);
      setError(null);
      try {
        const baseUrl = resolveApiOrigin();
        const response = await fetchWithAuth(`${baseUrl}/api/ai/suggest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenantId,
            assistiveMode: 'assistive_only',
            ...input,
          }),
        });
        const body = (await response.json()) as AssistiveApiEnvelope<AssistiveSuggestResult>;
        if (!response.ok || !body.success || !body.data) {
          throw new Error(body.error ?? 'AI 제안에 실패했습니다.');
        }
        setSuggestResult(body.data);
        return body.data;
      } catch (err: unknown) {
        const nextError = err instanceof Error ? err : new Error(String(err));
        setError(nextError);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [tenantId]
  );

  const reset = useCallback(() => {
    setSummary(null);
    setSuggestResult(null);
    setError(null);
  }, []);

  return { loading, error, summary, suggestResult, summarize, suggest, reset };
}
